import React from 'react';
import { i18n } from 'config/locales';
import { Summary, DateSummary, NameSummary } from 'components/Summary';

import { FOREIGN, FOREIGN_BUSINESS_VENTURES } from 'config/formSections/foreign';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { foreignBusinessVenturesAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import VenturesItem from './VenturesItem';

const sectionConfig = {
  key: FOREIGN_BUSINESS_VENTURES.key,
  section: FOREIGN.name,
  store: FOREIGN.store,
  subsection: FOREIGN_BUSINESS_VENTURES.name,
  storeKey: FOREIGN_BUSINESS_VENTURES.storeKey,
};

export class Ventures extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      HasForeignVentures: this.props.HasForeignVentures,
      List: this.props.List,
      ...queue,
    });
  };

  updateHasForeignVentures = (values) => {
    this.update({
      HasForeignVentures: values,
      List: values.value === 'Yes' ? this.props.List : { branch: {}, items: [] },
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  summary = (item, index) => {
    const obj = (item && item.Item) || {};
    const date = DateSummary(item.Dates);
    const name = NameSummary(obj.Name);

    return Summary({
      type: i18n.t('foreign.business.ventures.collection.summary.item'),
      index,
      left: name,
      right: date,
      placeholder: i18n.t('foreign.business.ventures.collection.summary.unknown'),
    });
  };

  render() {
    const isPVQ = isPVQForm(this.props.formType);
    const { errors, peopleWhoKnowYou, List } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, foreignBusinessVenturesAddressNameFields);
    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return isPVQ ? (
      <div
        className="section-content foreign-business-ventures"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_BUSINESS_VENTURES.key}
      >
        <h1 className="section-header">{i18n.t('foreign.pvq.subsection.business.ventures')}</h1>
        <Branch
          name="has_foreign_ventures"
          label={i18n.t('foreign.pvq.business.ventures.heading.title')}
          labelSize="h4"
          adjustFor="p"
          {...this.props.HasForeignVentures}
          warning
          onUpdate={this.updateHasForeignVentures}
          required={this.props.required}
          onError={this.handleError}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasForeignVentures"
        />

        {this.props.HasForeignVentures.value === 'Yes'
          && (
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.pvq.business.ventures.collection.summary.title')}
            appendTitle={i18n.t('foreign.pvq.business.ventures.collection.appendTitle')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <VenturesItem
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
              formType={this.props.formType}
            />
          </Accordion>
          )}
      </div>
    ) : (
      <div
        className="section-content foreign-business-ventures"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_BUSINESS_VENTURES.key}
      >
        <h1 className="section-header">{i18n.t('foreign.subsection.business.ventures')}</h1>
        <Branch
          name="has_foreign_ventures"
          label={i18n.t('foreign.business.ventures.heading.title')}
          labelSize="h4"
          adjustFor="p"
          {...this.props.HasForeignVentures}
          warning
          onUpdate={this.updateHasForeignVentures}
          required={this.props.required}
          onError={this.handleError}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasForeignVentures"
        >
          {i18n.t('foreign.business.ventures.para.branch')}
        </Branch>

        <Show when={this.props.HasForeignVentures.value === 'Yes'}>
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.business.ventures.collection.summary.title')}
            appendTitle={i18n.t('foreign.business.ventures.collection.appendTitle')}
            appendMessage={i18n.m('foreign.business.ventures.collection.appendMessage')}
            appendLabel={i18n.t('foreign.business.ventures.collection.append')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <VenturesItem
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
            />
          </Accordion>
        </Show>
      </div>
    );
  }
}

Ventures.defaultProps = {
  name: 'Ventures',
  HasForeignVentures: {},
  List: {},
  onUpdate: () => {},
  onError: (value, arr) => arr,
  section: 'foreign',
  subsection: 'business/ventures',
  addressBooks: {},
  dispatch: () => {},
  defaultState: true,
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(Ventures, sectionConfig);
